import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckSquare, ArrowRight, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { format, isAfter, isBefore, startOfDay, addDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { getPriorityColor } from '@/lib/todoHelpers';

interface DashboardTodo {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  charging_locations: { id: string; name: string } | null;
}

export function DashboardTodosWidget() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: todos = [], isLoading } = useQuery({
    queryKey: ['dashboard-todos', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unified_todos' as any)
        .select(`
          id,
          title,
          status,
          priority,
          due_date,
          charging_locations:location_id(id, name)
        `)
        .eq('assigned_to', user?.id)
        .is('deleted_at', null)
        .neq('status', 'completed')
        .is('project_id', null)
        .order('due_date', { ascending: true, nullsFirst: false })
        .limit(5);

      if (error) throw error;
      return (data || []) as unknown as DashboardTodo[];
    },
    enabled: !!user?.id,
  });

  const handleToggleComplete = async (todoId: string) => {
    const { error } = await supabase
      .from('unified_todos' as any)
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', todoId);

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['dashboard-todos'] });
      queryClient.invalidateQueries({ queryKey: ['my-charging-todos'] });
      toast({
        title: 'Succes',
        description: 'Taak voltooid',
      });
    }
  };

  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);

  const getDeadlineStatus = (dueDate: string | null) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    if (isBefore(date, today)) {
      return { label: 'Verlopen', variant: 'destructive' as const };
    }
    if (isBefore(date, tomorrow)) {
      return { label: 'Vandaag', variant: 'default' as const };
    }
    return null;
  };

  const urgentCount = todos.filter(t => 
    t.priority === 'urgent' || t.priority === 'high' ||
    (t.due_date && isBefore(new Date(t.due_date), today))
  ).length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckSquare className="h-5 w-5" />
            Mijn Taken
            {urgentCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {urgentCount} urgent
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/charging/todos" className="gap-1">
              Alle taken
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        ) : todos.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <CheckCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Geen openstaande taken!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todos.map((todo) => {
              const deadlineStatus = getDeadlineStatus(todo.due_date);
              
              return (
                <div
                  key={todo.id}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    checked={false}
                    onCheckedChange={() => handleToggleComplete(todo.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{todo.title}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge className={`text-xs ${getPriorityColor(todo.priority)}`}>
                        {todo.priority}
                      </Badge>
                      {deadlineStatus && (
                        <Badge variant={deadlineStatus.variant} className="text-xs gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {deadlineStatus.label}
                        </Badge>
                      )}
                      {todo.due_date && !deadlineStatus && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(new Date(todo.due_date), 'dd MMM', { locale: nl })}
                        </span>
                      )}
                      {todo.charging_locations && (
                        <span className="text-xs text-muted-foreground truncate">
                          📍 {todo.charging_locations.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
